import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useDeals } from '../../../store/DealsContext';
import { calculateDCF } from '../../../utils/calculations';
import { formatCurrency } from '../../../utils/formatters';
import { useModelExport } from '../../../hooks/useModelExport';

interface DCFAnalysisProps {
  onClose: () => void;
}

export default function DCFAnalysis({ onClose }: DCFAnalysisProps) {
  const { deals } = useDeals();
  const { exportToExcel, exporting } = useModelExport();
  const [selectedDeal, setSelectedDeal] = useState('');
  const [projectionYears, setProjectionYears] = useState(5);
  const [growthRate, setGrowthRate] = useState(5);
  const [wacc, setWacc] = useState(10);
  const [cashFlows, setCashFlows] = useState<number[]>([]);
  const [results, setResults] = useState<any>(null);

  const handleCalculate = () => {
    const deal = deals.find(d => d.id === selectedDeal);
    if (!deal) return;

    const initialCashFlow = deal.value * 0.1;
    const calculatedCashFlows = Array(projectionYears).fill(0).map((_, i) => {
      return initialCashFlow * Math.pow(1 + growthRate / 100, i + 1);
    });

    const dcfValue = calculateDCF(calculatedCashFlows, wacc, growthRate);
    setCashFlows(calculatedCashFlows);
    
    setResults({
      enterpriseValue: dcfValue,
      impliedEquityValue: dcfValue - (deal.value * 0.4),
      valuePerShare: (dcfValue - (deal.value * 0.4)) / 1000000
    });
  };

  const handleExport = () => {
    if (!results) return;
    exportToExcel({
      cashFlows,
      results,
      assumptions: { projectionYears, growthRate, wacc }
    }, 'dcf-analysis.xlsx');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">DCF Analysis</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Deal</label>
                <select
                  value={selectedDeal}
                  onChange={(e) => setSelectedDeal(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Select Deal</option>
                  {deals.map((deal) => (
                    <option key={deal.id} value={deal.id}>{deal.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Projection Years</label>
                <input
                  type="number"
                  value={projectionYears}
                  onChange={(e) => setProjectionYears(Number(e.target.value))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Growth Rate (%)</label>
                <input
                  type="number"
                  value={growthRate}
                  onChange={(e) => setGrowthRate(Number(e.target.value))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">WACC (%)</label>
                <input
                  type="number"
                  value={wacc}
                  onChange={(e) => setWacc(Number(e.target.value))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <button
                onClick={handleCalculate}
                disabled={!selectedDeal}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Calculate
              </button>
            </div>

            {results && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Results</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Enterprise Value</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(results.enterpriseValue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Implied Equity Value</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(results.impliedEquityValue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Value per Share</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(results.valuePerShare)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="w-full px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
                >
                  {exporting ? 'Exporting...' : 'Export to Excel'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}